import { onMounted, computed, watch } from "vue";
import { useBatchedCompletion } from "JIX/utils/completion.js";
import { badgeTypes } from "./constants";

export function useAutocompletions(userInput, autocompletionTypes) {
    const userInputTrimmed = computed(() => userInput.value.trimStart());

    const autcompletionThreshold = 1;
    const url = computed(() => {
        if (userInputTrimmed.value.length >= autcompletionThreshold) {
            const searchParams = new URLSearchParams({ q: userInputTrimmed.value });
            autocompletionTypes.forEach(type => searchParams.append('types', type));
            searchParams.append('lang', window.Stash.common.lang);
            return `/api/jobsearch/v3/autocomplete?${searchParams.toString()}`;
        }
        return null;
    });
    const { requestCompletionsUpdate, completions, searching } = useBatchedCompletion({
        query: url,
        delay: 300,
        fetchResults: (url, signal) => fetch(url, { signal }).then(resp => resp.json()),
    });

    const filteredCompletions = computed(() => {
        const out = {};
        for (let [type, completionResult] of Object.entries(completions.value)) {
            if (!completionResult.completions.length) {
                continue;
            }
            out[type] = completionResult; // contains title, completions
        }
        return out;
    });

    const order = ['employment_type', 'workinghours_type', 'employment_place', 'jobtitleid', 'subid', 'geoareaid', 'companyid', 'q'];
    const orderedSuggestions = computed(() => {
        const res = [];

        const found = new Set();
        for (const type of order) {
            if (filteredCompletions.value[type]?.completions.length) {
                res.push([type, filteredCompletions.value[type]]);
                found.add(type);
            }
        }

        // In case there are new ones we haven't defined an explicit order for
        for (const type of Object.keys(filteredCompletions.value)) {
            if (!found.has(type)) {
                res.push([type, filteredCompletions.value[type]]);
            }
        }

        return res;
    });


    watch(url, requestCompletionsUpdate, { immediate: true });


    return { requestCompletionsUpdate, filteredCompletions, orderedSuggestions, searching, url };
}

export function useUpdateQuery(onQueryChange, store) {
    /**
     * @param {{text: string, value: *}} suggestion
     * @param {{type: string}} metaData
     */
    function handleSelectSuggestion(suggestion, metaData) {
        store.commit('updateDynamicValueParameterMap', { type: metaData.type, text: suggestion.text, value: suggestion.value });
        store.dispatch('addParam', { key: metaData.type, value: suggestion.value });
        onQueryChange();
    }

    /**
     * @param {{value: number}} queryParam
     * @param {string} type
     */
    function handleRemoveChosenQueryParam(queryParam, type) {
        if (queryParam.badgeType === badgeTypes.NEARLOCATION) {
            store.commit('clearNearLocation');
            onQueryChange();
            return;
        }
        let list = store.getters.workingQuery[type];
        if (!Array.isArray(list)) {
            list = [list];
        }
        const newList = list.filter(x => x !== queryParam.value);
        store.commit('setParam', { key: type, value: newList });
        onQueryChange();
    }

    function handleRemoveChosenQueryParams(type) {
        store.commit('setParam', { key: type, value: [] });
        onQueryChange();
    }

    return { handleSelectSuggestion, handleRemoveChosenQueryParam, handleRemoveChosenQueryParams };
}

/**
 * Sets up a watcher, which sets a fitting max height on the dropdownElement when it opens
 * @param {ref} originElement The button or input field from which the dropdown expands from
 * @param {ref} dropdownElement The dropdown to change the max height of
 * @param {ref} dropdownOpen Ref indicating whether the dropdown is open
 */
export function setDropdownMaxHeightOnOpen(originElement, dropdownElement, dropdownOpen) {
    onMounted(() => {
        watch(() => dropdownOpen.value, () => {
            if (dropdownOpen.value && originElement.value) {
                const minHeightPx = 380;
                const roomForFooterPx = 90;
                const originRect = originElement.value.getBoundingClientRect();
                const dropDownMaxHeight = Math.max(window.innerHeight - originRect.bottom - roomForFooterPx, minHeightPx);
                dropdownElement.value.style.maxHeight = dropDownMaxHeight + 'px';
            }
        }, { flush: 'post' });
    });
}
